import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, map } from 'rxjs';
import {
  EstatusNotificacionTurno,
  NotificacionTurno,
  TurnoAsignadoEvento,
} from '../models/citas.models';

@Injectable({ providedIn: 'root' })
export class NotificacionesTurnosService {
  private readonly listaSubject = new BehaviorSubject<NotificacionTurno[]>([]);
  /** Comentario: emite cada notificación nueva en tiempo real (no al cargar storage) */
  private readonly nuevaSubject = new Subject<NotificacionTurno>();

  readonly lista$: Observable<NotificacionTurno[]> = this.listaSubject.asObservable();
  readonly nuevaNotificacion$: Observable<NotificacionTurno> = this.nuevaSubject.asObservable();

  /** Cantidad de notificaciones nuevas */
  readonly nuevasCount$: Observable<number> = this.lista$.pipe(
    map((lista) => lista.filter((n) => n.estatus === 'nuevo').length),
  );

  /** Notificaciones visibles agrupadas por tipo */
  readonly atencionTurnos$: Observable<NotificacionTurno[]> = this.lista$.pipe(
    map((lista) =>
      lista.filter((n) => n.tipo === 'atencion_turnos' && n.estatus !== 'borrado'),
    ),
  );

  constructor() {
    this.cargarDesdeStorage();
  }

  /** Clave de almacenamiento por usuario */
  private storageKey(): string {
    const uid = localStorage.getItem('usuario_id') || 'anon';
    return `sdm_notif_turnos_${uid}`;
  }

  /** Carga notificaciones persistidas */
  private cargarDesdeStorage(): void {
    try {
      const raw = localStorage.getItem(this.storageKey());
      if (raw) {
        const parsed = JSON.parse(raw) as NotificacionTurno[];
        this.listaSubject.next(Array.isArray(parsed) ? parsed : []);
      }
    } catch {
      this.listaSubject.next([]);
    }
  }

  /** Persiste lista actual */
  private guardar(): void {
    localStorage.setItem(this.storageKey(), JSON.stringify(this.listaSubject.value));
  }

  /** Agrega evento de turno asignado si no existe ya */
  agregarTurnoAsignado(payload: TurnoAsignadoEvento): void {
    const lista = [...this.listaSubject.value];
    const existe = lista.some(
      (n) => n.payload.turno_id === payload.turno_id && n.estatus !== 'borrado',
    );
    if (existe) {
      return;
    }
    const notif: NotificacionTurno = {
      id: `turno-${payload.turno_id}-${Date.now()}`,
      tipo: 'atencion_turnos',
      estatus: 'nuevo',
      recibida_en: new Date().toISOString(),
      payload,
    };
    lista.unshift(notif);
    this.listaSubject.next(lista);
    this.guardar();
    this.nuevaSubject.next(notif);
  }

  /** Marca notificación como vista */
  marcarVisto(id: string): void {
    const lista = this.listaSubject.value.map((n) =>
      n.id === id ? { ...n, estatus: 'visto' as EstatusNotificacionTurno } : n,
    );
    this.listaSubject.next(lista);
    this.guardar();
  }

  /** Marca como vista la notificación nueva del turno indicado */
  marcarVistoPorTurnoId(turnoId: number): void {
    let cambio = false;
    const lista = this.listaSubject.value.map((n) => {
      if (n.estatus === 'nuevo' && n.payload.turno_id === turnoId) {
        cambio = true;
        return { ...n, estatus: 'visto' as EstatusNotificacionTurno };
      }
      return n;
    });
    if (!cambio) {
      return;
    }
    this.listaSubject.next(lista);
    this.guardar();
  }

  /** Marca notificación como borrada */
  borrar(id: string): void {
    const lista = this.listaSubject.value.map((n) =>
      n.id === id ? { ...n, estatus: 'borrado' as EstatusNotificacionTurno } : n,
    );
    this.listaSubject.next(lista);
    this.guardar();
  }

  /** Recarga desde storage (tras cambio de usuario) */
  reiniciarSesion(): void {
    this.cargarDesdeStorage();
  }
}
